<template>
  <div
    v-if="!subscriptionIsFreeTrial && $store.state.idea.storyMode.common.completedSteps.profitAndCashFlowCompleted === true && isProfitCashFlowVisible"
    class="journal-content dynamic-height"
    :class="{'dynamic-height': !isOption(textData, 'incomeAndExpensesSection') && !isOption(textData, 'cashFlowSection') && !isOption(textData, 'incomeAndExpensesTableSection')}"
  >
    <div class="content-menus">
      <div v-if="moreVisible === true" class="view-type">
        <a
          v-if="showIncomeAndExpenses"
          class="orange"
          :class="{'orange-off': !isOption(textData, 'incomeAndExpensesSection')}"
          @click="enableIncomeAndExpenses"
        >
          {{ $t('pages.businessPlan.sections.profitAndCashFlow.incomeExpenses') }}
        </a>

        <a
          v-if="showCashFlow"
          class="orange"
          :class="{'orange-off': !isOption(textData, 'cashFlowSection')}"
          @click="enableCashFlow"
        >
          {{ $t('pages.businessPlan.sections.profitAndCashFlow.cashFlow') }}
        </a>
        <a
          v-if="showIncomeAndExpenses"
          class="orange"
          title="income / Expenses Summary"
          :class="{'orange-off': !isOption(textData, 'incomeAndExpensesTableSection')}"
          @click="enableIncomeAndExpensesTable"
        >
          {{ $t('pages.businessPlan.sections.profitAndCashFlow.iAndSummary') }}
        </a>

        <a
          v-if="showCashFlow"
          class="orange"
          title="Cash Flow Summary"
          :class="{'orange-off': !isOption(textData, 'cashFlowTableSection')}"
          @click="enableCashFlowTable"
        >
          {{ $t('pages.businessPlan.sections.profitAndCashFlow.cfSummary') }}
        </a>

        <p v-if="!showCashFlow && !showIncomeAndExpenses">
          {{ $t('pages.businessPlan.sections.youDontHaveAnyDataOnThisPage') }}
        </p>
      </div>
      <div v-if="canEdit" class="mini-actions">
        <toggle-page-items-button
          :visible="moreVisible"
          @click="toggleMore"
        />
        <toggle-view-button
          :disabled="!cardExist"
          :horizontal="!isOption(textData, 'gridListView')"
          @click="toggleRotate"
        />
        <toggle-notes-button
          :disabled="!moreDetails"
          :visible="isOption(textData, 'moreDetailsDisplay')"
          @click="enableMoreDetails"
        />
        <edit-section-link route-name="business-guide-projections-profit-and-cash-flow" />
      </div>
    </div>

    <div class="sub chapter">
      <header>
        <!-- Title section -->
        <editable-title
          v-model="textData.title"
          tag="h1"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Title section -->
        <hr>
        <!-- Description section -->
        <editable-description
          v-model="textData.subTitle"
          :empty-text="$t('addMoreDetails.addNoteText')"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Description section -->
      </header>

      <template v-if="showIncomeAndExpenses">
        <!-- Breakdown chart -->
        <div v-if="isOption(textData, 'incomeAndExpensesSection')" class="switcher-inner-wrapper breakdown-chart">
          <profit-and-cash-flow-profit-and-loss
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Breakdown chart -->
      </template>

      <template v-if="showCashFlow">
        <!-- Timeline chart -->
        <div v-if="isOption(textData, 'cashFlowSection')" class="switcher-inner-wrapper timeline-chart">
          <profit-and-cash-flow-cash-flow
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Timeline chart -->
      </template>

      <template v-if="showIncomeAndExpenses">
        <!-- Profit And Cash Flow Table -->
        <div v-if="isOption(textData, 'incomeAndExpensesTableSection')" class="switcher-inner-wrapper table-chart">
          <profit-and-loss-table
            :table-adjustment="tableAdjustment"
          />
        </div>
      </template>

      <template v-if="showCashFlow">
        <div v-if="isOption(textData, 'cashFlowTableSection')" class="switcher-inner-wrapper table-chart">
          <cash-flow-table
            :table-adjustment="tableAdjustment"
          />
        </div>
        <!-- /Profit And Cash Flow Table -->
      </template>

      <!-- More Details section -->
      <template v-if="moreDetails && isOption(textData, 'moreDetailsDisplay')">
        <div class="details">
          <editable-title
            v-model="textData.moreDetailsTitle"
            class="bold"
            tag="p"
            :disabled="!canEdit"
            @save="onSave"
          />
          <p v-html="moreDetails" />
        </div>
      </template>
      <!-- More Details section -->
    </div>
  </div>
</template>

<script>
import CashFlowTable from '@/views/Home/StoryMode/Forecast/ProfitAndCashFlow/Tables/CashFlowTable'
import EditSectionLink from '@/views/Home/StoryMode/Components/BusinessPlan/EditSectionLink'
import EditableDescription from '@/components/_v2/EditableDescription'
import EditableTitle from '@/components/_v2/EditableTitle'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinUserSubscription from '@/mixins/permissions/userSubscription'
import ProfitAndCashFlowCashFlow
  from '@/views/Home/StoryMode/Forecast/ProfitAndCashFlow/Charts/ProfitAndCashFlowCashFlow'
import ProfitAndCashFlowProfitAndLoss
  from '@/views/Home/StoryMode/Forecast/ProfitAndCashFlow/Charts/ProfitAndCashFlowProfitAndLoss'
import ProfitAndLossTable from '@/views/Home/StoryMode/Forecast/ProfitAndCashFlow/Tables/ProfitAndLossTable'
import ToggleNotesButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleNotesButton'
import TogglePageItemsButton from '@/views/Home/StoryMode/Components/BusinessPlan/TogglePageItemsButton'
import ToggleViewButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleViewButton'
import journalMethods from '@/mixins/journalMethods'

export default {
  name: 'ProfitAndCashFlow',

  components: {
    CashFlowTable,
    EditSectionLink,
    EditableDescription,
    EditableTitle,
    ProfitAndCashFlowCashFlow,
    ProfitAndCashFlowProfitAndLoss,
    ProfitAndLossTable,
    ToggleNotesButton,
    TogglePageItemsButton,
    ToggleViewButton
  },

  mixins: [
    journalMethods,
    MixinIdeaRoles,
    MixinUserSubscription
  ],

  props: {
    allPagesTextData: {
      type: Object,
      default: () => ({})
    },
    shareMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      cardExist: false,

      moreVisible: false,
      updateCharts: true,
      inputHover: false,

      moreDetails: this.$store.state.idea.storyMode.common.moreDetails.profitAndCashFlowMoreDetails,

      chartAdjustment: {
        chartHeight: 260,
        headingTitleFontSize: '18px',
        xLabelTitleFontSize: '10px',
        yLabelTitleFontSize: '12px',
        yaxisLabelsMinWidth: {
          breakDown: 0,
          timeLine: 0
        }
      },
      tableAdjustment: {
        journalPage: true,
        period: 'yearly',
        tableFirstColumnWidth: 250,
        columnWidth: 114
      },
      textData: {}
    }
  },

  computed: {
    showIncomeAndExpenses () {
      return this.$store.state.idea.storyMode.forecast.revenueStreams.revenueStreams.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.directCosts.directCosts.length !== 0 ||
        this.$store.state.idea.storyMode.setUp.staffAndPayrolls.staffAndPayrolls.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.marketingBudget.marketingElements.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.otherOverheads.otherOverheads.length !== 0 ||
        this.$store.state.idea.storyMode.setUp.startupCosts.startupCosts.length !== 0 ||
        this.$store.state.idea.storyMode.setUp.financing.finances.length !== 0
    },

    showCashFlow () {
      return this.$store.state.idea.storyMode.forecast.revenueStreams.revenueStreams.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.directCosts.directCosts.length !== 0 ||
        this.$store.state.idea.storyMode.setUp.staffAndPayrolls.staffAndPayrolls.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.marketingBudget.marketingElements.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.otherOverheads.otherOverheads.length !== 0 ||
        this.$store.state.idea.storyMode.setUp.startupCosts.startupCosts.length !== 0 ||
        this.$store.state.idea.storyMode.setUp.financing.finances.length !== 0 ||
        this.$store.state.idea.storyMode.forecast.profitAndCashFlow.dividends.length !== 0
    },

    isProfitCashFlowVisible () {
      const index = this.$store.state.idea.journal.journalSwitchBars.indexOf(
        this.$store.state.idea.journal.journalSwitchBars.find(journalElement => journalElement.sectionName === 'ProfitAndCashFlow')
      )

      return !this.$store.state.idea.journal.journalSwitchBars[index].options.includes('switch')
    },

    isTitleEnabled () {
      return this.textData.title
    },

    isMoreDetailsTitleEnabled () {
      return this.textData.moreDetailsTitle
    }
  },

  watch: {
    allPagesTextData () {
      this.textData = Object.assign({}, this.allPagesTextData)
    }
  },

  mounted () {
    this.textData = Object.assign({}, this.allPagesTextData)
  },

  methods: {
    toggleRotate () {
      if (this.cardExist) {
        this.toggleOption(this.textData, 'gridListView')
        this.textData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    enableMoreDetails () {
      if (this.moreDetails) {
        this.toggleOption(this.textData, 'moreDetailsDisplay')
        this.textData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    onSave () {
      this.$emit('save', this.textData)
    },

    enableCashFlow () {
      this.toggleOption(this.textData, 'cashFlowSection')
      this.textData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableIncomeAndExpenses () {
      this.toggleOption(this.textData, 'incomeAndExpensesSection')
      this.textData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableIncomeAndExpensesTable () {
      this.toggleOption(this.textData, 'incomeAndExpensesTableSection')
      this.textData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableCashFlowTable () {
      this.toggleOption(this.textData, 'cashFlowTableSection')
      this.textData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    }
  }
}
</script>
